import { MutableRefObject, useCallback, useEffect, useRef, useState } from "react";
import { Pagination, Navigation } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import { IGif } from "../../services/types";
import { metrics, ViewRegion } from "../../utils/metrics";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import "./GallerySlider.scss";

interface IProps {
  gallery: IGif[];
  slidePrevArrow: MutableRefObject<any>;
  slideNextArrow: MutableRefObject<any>;
  sliderPagination: MutableRefObject<HTMLDivElement | null>;
  fullscreen: boolean;
  position: number;
  source: ViewRegion;
  setGalleryGifId: React.Dispatch<React.SetStateAction<string | null>>;
}

export const GallerySlider = (props: IProps) => {
  const {
    slidePrevArrow,
    slideNextArrow,
    setGalleryGifId,
    gallery,
    sliderPagination,
    fullscreen,
    source,
    position
  } = props;

  const [swiper, setSwiper] = useState<any>(null);
  const isSentMetrics = useRef<string[]>([]);

  const navigationSettings = {
    prevEl: slidePrevArrow.current,
    nextEl: slideNextArrow.current
  };

  useEffect(() => {
    if (swiper) {
      swiper.params.navigation.prevEl = slidePrevArrow.current;
      swiper.params.navigation.nextEl = slideNextArrow.current;

      setGalleryGifId(gallery[swiper.activeIndex].id);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [swiper]);

  const paginationSettings = {
    el: sliderPagination.current,
    clickable: true,
    renderBullet: (index: number, className: string) => {
      return `<div class="dot ${className}"></div>`;
    },
  };

  const sendMetrics = useCallback((index: number) => {
    if (index === 0) {
      return;
    }

    if (!gallery[index]) {
      return;
    }

    const gif = gallery[index];

    if (isSentMetrics.current.indexOf(gif.id) === -1) {
      metrics.sendGifView({
        gifId: gif.id,
        tags: gif.tags,
        fullscreen: fullscreen,
        source: source,
        position: position
      });

      isSentMetrics.current.push(gif.id);
    }
  }, [fullscreen, gallery, source, position]);

  const handleSlideChange = useCallback((swiper: any) => {
    sendMetrics(swiper.activeIndex);
    setGalleryGifId(gallery[swiper.activeIndex].id);
  }, [sendMetrics, gallery, setGalleryGifId]);

  const renderSlide = (item: IGif) => (
    <SwiperSlide key={item.id}>
      <img
        src={item.urls.hd ?? item.urls.sd}
        className="thumbnail"
        alt=""
        style={{
          aspectRatio: `${item.width} / ${item.height}`,
        }}
      />
    </SwiperSlide>
  );

  const renderSlides = gallery.map(renderSlide);

  return (
    <div className="GallerySlider">
      <Swiper
        slidesPerView={1}
        modules={[Navigation, Pagination]}
        navigation={navigationSettings}
        pagination={paginationSettings}
        onSwiper={setSwiper}
        onSlideChange={handleSlideChange}
      >
        {renderSlides}
      </Swiper>
    </div>
  );
};
